import { useMemo } from 'react';
import { queries } from '@strength/zero-schema';
import { useZeroQuery } from '../../../../zero/useZeroQuery';
import { trpc } from '../../../../trpc/react';
import { useIsZeroSurfaceActive } from '../../../zero/zero-active';
import { getUrlTeam, useViewer } from '../../../context/viewer';
import { sortTracks } from '../sort-tracks';
import type { TrackRow } from '../timeline/types';

type UseTracksQueryResult = {
  tracks: TrackRow[];
  refetch: () => void;
};

export function useTracksQuery(teamId: string): UseTracksQueryResult {
  const isZeroActive = useIsZeroSurfaceActive('tracks');

  // `plan.tracksForTeam` is a teamProcedure whose scope arrives only via the
  // URL-derived `tw-team-ids` header, so enablement must gate on the same
  // URL value — see the invariant in app/context/viewer.tsx. Without this, the
  // Plan page (still mounted for a beat after navigating to a team-agnostic
  // route) refetches with no header and the server 400s.
  const hasUrlTeam = getUrlTeam(window.location.pathname) !== '';

  // useZeroQuery is called unconditionally (Rules of Hooks). The query is
  // passed only when the tracks surface is active: another surface's flag can
  // mount the provider while this surface stays on tRPC, and a live query
  // handed over then would subscribe and populate the cache anyway.
  const [rawTracks] = useZeroQuery(
    isZeroActive
      ? queries.track.tracksForTeam({ teamIds: [teamId] })
      : undefined,
  );

  // tRPC query — also called unconditionally. It is the fallback track source
  // when Zero is inactive, and the scaffolding source for a Blocks-only coach
  // (see scaffoldForBlocks below), since Zero suppresses the Track model
  // server-side when Tracks View is revoked. The Actuals page also consumes
  // the `plan.tracksForTeam` route as a means to find blocks — that use is
  // not tracks viewing, so the route itself stays ungoverned and the gate
  // lives here, at the Plan surface.
  const { hasPermissions } = useViewer();
  const canViewTracks = hasPermissions('plan.tracks', 'view');
  // A Blocks-only coach still needs track scaffolding: the timeline is one
  // row per track and blocks render inside their track's row, so without
  // track rows there is nothing to place blocks in. The ungoverned
  // `plan.tracksForTeam` route serves the scaffolding — finding blocks is
  // not tracks viewing (same precedent as the Actuals lookup) — and names
  // are redacted below so no track identity renders anywhere on the surface.
  // Under Zero the Track model itself is suppressed server-side when Tracks
  // View is revoked, so the scaffolding always comes from tRPC.
  const canViewBlocks = hasPermissions('plan.blocks', 'view');
  const scaffoldForBlocks = !canViewTracks && canViewBlocks;

  const trpcQuery = trpc.plan.tracksForTeam.useQuery(undefined, {
    staleTime: 10_000,
    enabled: hasUrlTeam && (canViewTracks ? !isZeroActive : scaffoldForBlocks),
  });

  const zeroTracks = useMemo(() => {
    if (!rawTracks) return [];
    // normalise sortOrder: Zero types it as number | null (optional in the
    // generated schema) but the DB column is notNull/default 0. TrackRow
    // requires number.
    return sortTracks(
      rawTracks.map((t) => ({
        ...t,
        sortOrder: t.sortOrder ?? 0,
        archivedAt: t.archivedAt ?? null,
      })),
    );
  }, [rawTracks]);

  // Neither View means no track data may surface at all — not even
  // scaffolding. A disabled query still serves whatever is already in the
  // cache, and the `plan.tracksForTeam` key is team-agnostic — so after a
  // mid-session revocation `trpcQuery.data` could hand back every track the
  // viewer may no longer see. Gate before any other return.
  if (!canViewTracks && !canViewBlocks) {
    return { tracks: [], refetch: () => {} };
  }

  // Blocks-only: render the timeline from redacted scaffolding. Names are
  // stripped even from cached payloads so no track identity can reach a
  // renderer (the tracks column shows the planner's empty-state message, and
  // the calendar filter gets no names either).
  if (scaffoldForBlocks) {
    return {
      tracks: (trpcQuery.data ?? []).map((t) => ({
        ...t,
        name: '',
      })) as TrackRow[],
      refetch: trpcQuery.refetch,
    };
  }

  if (isZeroActive) {
    return { tracks: zeroTracks, refetch: () => {} };
  }

  if (!hasUrlTeam) {
    return { tracks: [], refetch: () => {} };
  }

  return {
    tracks: (trpcQuery.data ?? []) as TrackRow[],
    refetch: trpcQuery.refetch,
  };
}
